export default function Privacy() {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Content Section */}
      <section className="py-20 px-6 lg:px-40 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Privacy Content */}
          <div className="prose max-w-none space-y-8">
            {/* Section 1 */}
            <div>
              <h2 className="text-3xl font-bold text-black mb-4 flex items-center">
                <span className="inline-block w-1 h-8 bg-primary rounded-full mr-4"></span>
                1. Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We value the trust our users and customers place in us when they
                give us access to their personal data. This Privacy Policy
                describes how we work to maintain that trust and protect that
                information.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy details our collection, use and disclosure
                of personal and non-personal data you provide when you access or
                use our online and mobile services and websites (collectively,
                the &quot;Site&quot;.
              </p>
            </div>

            {/* Section 1.2 */}
            <div>
              <h3 className="text-2xl font-bold text-black mb-4">
                1.2 Personal Data and Non-Personal Data
              </h3>
              <p className="text-gray-700 leading-relaxed">
                <span className="text-primary font-semibold">
                  Personal data
                </span>{" "}
                refers to any information that relates to an identified or
                identifiable individual.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <span className="text-primary font-semibold">
                  Non-personal data
                </span>{" "}
                refers to any information that does not identify you as a
                specific individual.
              </p>
            </div>

            {/* Section 2 */}
            <div>
              <h2 className="text-3xl font-bold text-black mb-4 flex items-center">
                <span className="inline-block w-1 h-8 bg-primary rounded-full mr-4"></span>
                2. Information Collected
              </h2>

              <h3 className="text-2xl font-bold text-black mb-4 mt-6">
                2.1 Information You Provide
              </h3>
              <ul className="space-y-3 mb-6 ml-4">
                <li className="text-gray-700 flex items-start">
                  <span className="text-primary mr-3 mt-1">•</span>
                  <span>
                    <span className="text-primary font-semibold">
                      Account Information:
                    </span>{" "}
                    Username, password, name, email, phone, address, occupation,
                    interests, and resume details
                  </span>
                </li>
                <li className="text-gray-700 flex items-start">
                  <span className="text-primary mr-3 mt-1">•</span>
                  <span>
                    <span className="text-primary font-semibold">
                      Activity Data:
                    </span>{" "}
                    Job searches, titles clicked on, locations, salary
                    interests, viewing history, and time spent on site
                  </span>
                </li>
                <li className="text-gray-700 flex items-start">
                  <span className="text-primary mr-3 mt-1">•</span>
                  <span>
                    <span className="text-primary font-semibold">
                      Employer Information:
                    </span>{" "}
                    Business registration, utility bills, representative
                    details, and payment information
                  </span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-black mb-4 mt-6">
                2.2 Third-Party Information
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Any Personal Data we may obtain from other third-party sources
                will be processed in accordance with this Privacy Policy and all
                applicable laws.
              </p>
            </div>

            {/* Section 3 */}
            <div>
              <h2 className="text-3xl font-bold text-black mb-4 flex items-center">
                <span className="inline-block w-1 h-8 bg-primary rounded-full mr-4"></span>
                3. How We Use Your Information
              </h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                We use your information to help Job Seekers find jobs and
                Employers find candidates. More specifically:
              </p>

              <div className="space-y-4">
                <div className="p-4 backdrop-blur-lg bg-gray-50 border border-primary/30 rounded-lg">
                  <h4 className="text-black font-semibold mb-2">
                    ✓ Providing Our Services
                  </h4>
                  <p className="text-gray-700 text-sm">
                    We use your data to create accounts, send job alerts, and
                    match Job Seekers with Employers.
                  </p>
                </div>

                <div className="p-4 backdrop-blur-lg bg-gray-50 border border-primary/30 rounded-lg">
                  <h4 className="text-black font-semibold mb-2">
                    ✓ Improving Our Services
                  </h4>
                  <p className="text-gray-700 text-sm">
                    We measure and improve our platform by analyzing your usage
                    patterns and providing personalized recommendations.
                  </p>
                </div>

                <div className="p-4 backdrop-blur-lg bg-gray-50 border border-primary/30 rounded-lg">
                  <h4 className="text-black font-semibold mb-2">
                    ✓ Communications
                  </h4>
                  <p className="text-gray-700 text-sm">
                    We may contact you via email, phone, or postal mail about
                    services, promotions, and interview notifications.
                  </p>
                </div>

                <div className="p-4 backdrop-blur-lg bg-gray-50 border border-primary/30 rounded-lg">
                  <h4 className="text-black font-semibold mb-2">
                    ✓ Fraud Prevention
                  </h4>
                  <p className="text-gray-700 text-sm">
                    We monitor activity to prevent fraud, spam, and illegal
                    activities on our platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div>
              <h2 className="text-3xl font-bold text-black mb-4 flex items-center">
                <span className="inline-block w-1 h-8 bg-primary rounded-full mr-4"></span>
                4. Your Rights
              </h2>

              <h3 className="text-xl font-semibold text-black mb-3 mt-6">
                4.1 Account Closure
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you close your account, you will no longer have access to
                your search history, saved jobs, applications, and resume.
                However, we may retain certain information to comply with legal
                obligations or to protect our rights.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 mt-6">
                4.2 Data Storage
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We retain your Personal Data to the extent necessary for the
                purposes for which it is processed. We store information
                securely using appropriate protections as required by applicable
                laws.
              </p>
            </div>

            {/* Key Principles */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 backdrop-blur-2xl bg-gradient-to-br from-primary/10 to-primary-fixed/10 border border-primary/50 rounded-2xl">
                <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
                  <span className="text-2xl mr-2">🔒</span> Data Protection
                </h3>
                <p className="text-gray-700 text-sm">
                  Your personal information is protected with encryption and
                  secure storage protocols.
                </p>
              </div>

              <div className="p-6 backdrop-blur-2xl bg-gradient-to-br from-primary/10 to-primary-fixed/10 border border-primary/50 rounded-2xl">
                <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
                  <span className="text-2xl mr-2">📋</span> Transparency
                </h3>
                <p className="text-gray-700 text-sm">
                  We clearly communicate what data we collect and how it&apos;s
                  used in this policy.
                </p>
              </div>

              <div className="p-6 backdrop-blur-2xl bg-gradient-to-br from-primary/10 to-primary-fixed/10 border border-primary/50 rounded-2xl">
                <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
                  <span className="text-2xl mr-2">⚙️</span> Control
                </h3>
                <p className="text-gray-700 text-sm">
                  You have control over your account settings and information
                  visibility on our platform.
                </p>
              </div>

              <div className="p-6 backdrop-blur-2xl bg-gradient-to-br from-primary/10 to-primary-fixed/10 border border-primary/50 rounded-2xl">
                <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
                  <span className="text-2xl mr-2">📧</span> Contact Us
                </h3>
                <p className="text-gray-700 text-sm">
                  If you have questions about this policy, please reach out to
                  our support team.
                </p>
              </div>
            </div>

            {/* Notice */}
            <div className="mt-12 p-8 backdrop-blur-2xl bg-gradient-to-br from-primary/10 to-primary-fixed/10 border border-primary/50 rounded-2xl">
              <h3 className="text-xl font-semibold text-black mb-4">
                Minimum Age Requirement
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Our platform is not for use by anyone under the age of 18. To
                use the Site, you agree that you are at least 18 years old, or
                the applicable minimum age in your jurisdiction if higher.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
